
import { useState } from 'react';
import { features } from '../../../mocks/featuresData';

export default function FeatureShowcase() {
  const [expandedFeature, setExpandedFeature] = useState<string | null>(null);

  const getFeatureIcon = (id: string) => {
    const icons: Record<string, string> = {
      'lead-generation': 'ri-user-add-line',
      'chatbot-voice': 'ri-robot-line',
      'landing-funnel': 'ri-pages-line',
      'crm-system': 'ri-contacts-book-line',
      'multi-user': 'ri-team-line',
      'academy': 'ri-graduation-cap-line',
      'ai-content': 'ri-magic-line',
      'calendar-booking': 'ri-calendar-check-line',
      'payment-subscriptions': 'ri-bank-card-line',
      'ecommerce': 'ri-shopping-bag-line',
      'realtime-notifications': 'ri-notification-badge-line',
      'file-storage': 'ri-folder-cloud-line'
    };
    return icons[id] || 'ri-apps-line';
  };

  const getFeatureTags = (id: string) => {
    const tags: Record<string, string[]> = {
      'lead-generation': ['Supabase', 'Google Places API', 'Hunter API', 'Lead-Scoring'],
      'chatbot-voice': ['Readdy Agent', 'Voice & Chat', 'Calendly', '24/7 Support'],
      'landing-funnel': ['Stripe', 'Calendly', 'A/B-Testing', 'Conversion-Tracking'],
      'crm-system': ['Supabase', 'Stripe', 'Pipeline', 'Echtzeit-Sync'],
      'multi-user': ['Supabase Auth', 'Rollen', 'Team-Kollaboration', 'SSO'],
      'academy': ['Video-Hosting', 'Stripe', 'Zertifikate', 'Fortschritt'],
      'ai-content': ['OpenAI API', 'Templates', 'Multi-Plattform', 'Scheduling'],
      'calendar-booking': ['Calendly', 'Zeitzonen', 'Erinnerungen', 'Sync'],
      'payment-subscriptions': ['Stripe', 'Abonnements', 'Rechnungen', 'SEPA'],
      'ecommerce': ['Shopify', 'Inventar', 'Multi-Channel', 'Versand'],
      'realtime-notifications': ['Supabase Realtime', 'WebSocket', 'Push', 'Live-Updates'],
      'file-storage': ['Supabase Storage', 'CDN', 'Bildoptimierung', 'Backups']
    };
    return tags[id] || [];
  };

  const getGradientColor = (index: number) => {
    const gradients = [
      'from-teal-500 to-emerald-500',
      'from-cyan-500 to-teal-500',
      'from-emerald-500 to-green-500',
      'from-teal-400 to-cyan-500',
      'from-green-500 to-teal-500',
      'from-cyan-400 to-emerald-500',
      'from-teal-500 to-green-500',
      'from-emerald-400 to-teal-500',
      'from-green-400 to-emerald-500',
      'from-teal-400 to-green-500',
      'from-cyan-500 to-green-500',
      'from-emerald-500 to-cyan-500'
    ];
    return gradients[index % gradients.length];
  };

  return (
    <section id="features" className="py-32 bg-dark relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/3 rounded-full blur-3xl"></div>

      <div className="max-w-[1400px] mx-auto px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">
            12 leistungsstarke Module
          </span>
          <h2 className="text-5xl font-bold text-white mb-6">
            Alles was Sie für Ihr Business brauchen
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Entdecken Sie unsere umfassende Suite an Tools, die mit modernsten Technologien wie Supabase, Stripe, Shopify, Calendly und KI-Integration realisiert werden.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={`group relative bg-dark-lighter/50 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden transition-all duration-500 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/10 ${
                expandedFeature === feature.id ? 'lg:col-span-2 lg:row-span-2' : ''
              }`}
            >
              {/* Gradient Overlay on Hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${getGradientColor(index)} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>
              
              <div className="relative p-8">
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getGradientColor(index)} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${getFeatureIcon(feature.id)} text-2xl text-white`}></i>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-primary transition-colors duration-300">
                  {feature.title}
                </h3>

                {/* Tooltip/Short Description */}
                <p className="text-gray-400 text-sm mb-5 leading-relaxed">
                  {feature.tooltip}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {getFeatureTags(feature.id).slice(0, 3).map((tag, tagIndex) => (
                    <span
                      key={tagIndex}
                      className="px-3 py-1 bg-white/5 text-gray-300 rounded-full text-xs font-medium border border-white/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Expand Button */}
                <button
                  onClick={() => setExpandedFeature(expandedFeature === feature.id ? null : feature.id)}
                  className="flex items-center gap-2 text-primary text-sm font-medium cursor-pointer hover:gap-3 transition-all duration-300"
                >
                  <span>{expandedFeature === feature.id ? 'Weniger anzeigen' : 'Mehr erfahren'}</span>
                  <i className={`ri-arrow-right-line transition-transform duration-300 ${expandedFeature === feature.id ? 'rotate-90' : ''}`}></i>
                </button>

                {/* Expanded Content */}
                {expandedFeature === feature.id && (
                  <div className="mt-6 pt-6 border-t border-white/10 animate-fade-in">
                    <p className="text-gray-300 leading-relaxed mb-6">
                      {feature.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {getFeatureTags(feature.id).map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="px-4 py-2 bg-primary/20 text-primary rounded-full text-sm font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Corner Decoration */}
              <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
                <div className={`absolute -top-10 -right-10 w-20 h-20 bg-gradient-to-br ${getGradientColor(index)} opacity-20 rotate-45`}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-4 px-8 py-4 bg-dark-lighter/50 rounded-2xl border border-white/10">
            <div className="flex -space-x-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
                <i className="ri-database-2-line text-white text-sm"></i>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                <i className="ri-bank-card-line text-white text-sm"></i>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                <i className="ri-shopping-bag-line text-white text-sm"></i>
              </div>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center">
                <i className="ri-brain-line text-white text-sm"></i>
              </div>
            </div>
            <div className="text-left">
              <p className="text-white font-semibold">Powered by führenden Technologien</p>
              <p className="text-gray-400 text-sm">Supabase • Stripe • Shopify • Calendly • OpenAI</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
